<template>
  <header class="mobile-header-controls fixed top-[5px] left-0 w-full pl-[5px] pr-[5px] flex items-start justify-between pointer-events-none z-30">
    <!-- Back Button (left side) — Figma 4783:134648 -->
    <div class="pointer-events-auto">
      <button
        type="button"
        class="header-pill-btn header-pill-btn--single"
        aria-label="Go back"
        @click="handleBack"
      >
        <span class="icon-wrap icon-wrap--back">
          <img src="/new-studio/mobile/icon-back.svg" alt="" />
        </span>
        <span class="btn-label">Back</span>
      </button>
    </div>

    <!-- Right Action Buttons (View Mode Toggle, Change Grid, View in Room, More) -->
    <div class="flex items-center gap-[5px] pointer-events-auto">
      <!-- 2D / 3D View Mode Toggle (3D Grid only) -->
      <button
        v-if="is3DGrid"
        type="button"
        class="header-pill-btn header-pill-btn--view-toggle"
        role="switch"
        :aria-checked="gridStore.showDepthPreview"
        aria-label="Toggle 3D viewing mode"
        @click="gridStore.setShowDepthPreview(!gridStore.showDepthPreview)"
      >
        <span class="view-toggle-track">
          <span class="view-toggle-thumb">
            <span class="view-toggle-thumb-label">{{ gridStore.showDepthPreview ? '3D' : '2D' }}</span>
          </span>
        </span>
        <span class="btn-label btn-label--toggle">View<br />Mode</span>
      </button>

      <!-- Change Grid -->
      <button
        type="button"
        class="header-pill-btn header-pill-btn--double"
        :class="{ active: showMobileGridOptions }"
        aria-label="Change grid pattern"
        @click="showMobileGridOptions = !showMobileGridOptions"
      >
        <span class="icon-wrap icon-wrap--grid">
          <img src="/new-studio/mobile/icon-grid.svg" alt="" />
        </span>
        <span class="btn-label btn-label--double">Change<br />Grid</span>
      </button>

      <!-- View in Room -->
      <button
        type="button"
        class="header-pill-btn header-pill-btn--double"
        :disabled="!hasShapes"
        aria-label="View in Room"
        @click="$emit('viewInRoom')"
      >
        <span class="icon-wrap icon-wrap--grid">
          <img src="/new-studio/mobile/icon-view-in-room.svg" alt="" />
        </span>
        <span class="btn-label btn-label--double">View in<br />Room</span>
      </button>

      <!-- More -->
      <div class="relative">
        <button
          type="button"
          class="header-pill-btn header-pill-btn--single"
          :class="{ active: moreMenuOpen }"
          aria-label="More options"
          @click="moreMenuOpen = !moreMenuOpen"
        >
          <span class="icon-wrap icon-wrap--more">
            <img src="/new-studio/mobile/icon-more.svg" alt="" />
          </span>
          <span class="btn-label">More</span>
        </button>

        <!-- More Menu Dropdown (Figma 4909:143368) -->
        <Transition name="fade-menu">
          <div
            v-if="moreMenuOpen"
            v-click-outside="closeMoreMenu"
            class="more-menu-dropdown absolute top-[64px] right-0 bg-white rounded-lg p-1.5 z-40"
          >
            <button
              v-for="item in menuItems"
              :key="item.label"
              type="button"
              class="menu-item-btn flex items-center gap-3 w-full px-3 py-2.5 rounded-[10px] text-left hover:bg-[#F5F5F3] active:bg-[#ECEBE6] transition-colors"
              @click="item.action"
            >
              <span class="w-[22px] h-[22px] flex items-center justify-center shrink-0">
                <img :src="item.icon" :alt="item.label" class="w-4 h-4 object-contain" />
              </span>
              <span class="text-[12.5px] font-semibold text-[#1C1C1E] leading-[15px]">
                {{ item.label }}
              </span>
            </button>
          </div>
        </Transition>
      </div>
    </div>
    <!-- Mobile Grid Options Modal (Figma 4832:137202).
         Shares its open/closed state and the "available grids" highlight
         with the desktop's GridOptionsPopover via useState, so the shape
         strip's availability link can open the same modal. -->
    <ModalMobileGridOptionsModal
      :show="showMobileGridOptions"
      :highlighted-grid-ids="highlightedGridIds"
      @close="showMobileGridOptions = false"
    />
  </header>
</template>

<script setup lang="ts">
import { GRID_PATTERN_IDS } from '~/composables/grid/constants'
import { ModalType, useModalStore } from '~/stores/modal'
import { useUserStore } from '~/stores/user'

const emit = defineEmits<{ viewInRoom: [] }>()
const router = useRouter()
const modalStore = useModalStore()
const gridStore = useGridStore()
const projectStore = useProjectStore()
const userStore = useUserStore()
const moreMenuOpen = ref(false)
// Shared with the desktop GridOptionsPopover (composables/useNewStudioActions)
// so the shape strip's availability link can open the same modal.
const showMobileGridOptions = useState<boolean>('new-studio-grid-options-open', () => false)
const highlightedGridIds = useState<string[]>('new-studio-highlighted-grids', () => [])

const is3DGrid = computed(() => gridStore.gridPattern?.id === GRID_PATTERN_IDS.THREE_D)
const hasShapes = computed(() => (gridStore.shapeList?.length ?? 0) > 0)

const closeMoreMenu = () => {
  moreMenuOpen.value = false
}

// Runs after a successful login from the Login modal (the `callback` option),
// so signing in from "Your Designs" lands straight on the designs list.
const handleMyDesigns = () => {
  modalStore.toggleModal(ModalType.MyDesigns)
}

const handleBack = () => {
  if (window.history.length > 1) {
    router.back()
  } else {
    router.push('/')
  }
}

const menuItems = computed(() => [
  {
    label: 'Preview',
    icon: '/new-studio/mobile/menu-preview.svg',
    action: () => {
      moreMenuOpen.value = false
      // Mirrors the desktop Preview button in `components/NewStudio/TopToolbar.vue`,
      // which calls `actions.preview = () => navigateTo('/preview/${designId || 0}')`.
      // The original wire-up emitted `viewInRoom`, which opened the AR viewer
      // (no preview). Reusing the desktop routing so mobile and desktop share
      // the same /preview/{designId} page.
      navigateTo(`/preview/${projectStore.designId || 0}`)
    }
  },
  {
    label: 'New',
    icon: '/new-studio/mobile/menu-new.svg',
    action: () => {
      moreMenuOpen.value = false
      modalStore.toggleModal(ModalType.New)
    }
  },
  {
    label: 'Load',
    icon: '/new-studio/mobile/menu-load.svg',
    action: () => {
      moreMenuOpen.value = false
      modalStore.toggleModal(ModalType.Load)
    }
  },
  {
    label: 'Save',
    icon: '/new-studio/mobile/menu-save.svg',
    action: () => {
      moreMenuOpen.value = false
      modalStore.toggleModal(ModalType.SaveDesign)
    }
  },
  {
    label: 'Save As',
    icon: '/new-studio/mobile/menu-save-as.svg',
    action: () => {
      moreMenuOpen.value = false
      modalStore.toggleModal(ModalType.SaveAs)
    }
  },
  {
    label: 'Your Designs',
    icon: '/new-studio/mobile/menu-your-designs.svg',
    action: () => {
      moreMenuOpen.value = false
      // Same guard as the desktop tools bar (ProjectTools/index.vue):
      // prompt for the email first, then open My Designs once signed in.
      // Opening it unauthenticated made fetchDesigns throw, which left the
      // modal stuck on its loading spinner forever.
      if (!userStore.isLoggedIn) {
        modalStore.toggleModal(ModalType.Login, { callback: handleMyDesigns })
        return
      }
      modalStore.toggleModal(ModalType.MyDesigns)
    }
  },
  {
    label: 'Settings',
    icon: '/new-studio/mobile/menu-settings.svg',
    action: () => {
      moreMenuOpen.value = false
      modalStore.toggleModal(ModalType.File)
    }
  },
  {
    label: 'Share',
    icon: '/new-studio/mobile/menu-share.svg',
    action: () => {
      moreMenuOpen.value = false
      modalStore.toggleModal(ModalType.Share)
    }
  },
  {
    // Mirrors the desktop tools bar (ProjectTools/index.vue and
    // NewStudio/TopToolbar.vue): the same control reads "Logout" once
    // authenticated, and clicking it signs out instead of reopening the
    // login modal.
    label: userStore.isLoggedIn ? 'Logout' : 'Login',
    icon: '/new-studio/mobile/menu-login.svg',
    action: () => {
      moreMenuOpen.value = false
      if (userStore.isLoggedIn) {
        userStore.logout()
      } else {
        modalStore.toggleModal(ModalType.Login)
      }
    }
  }
])

// Custom click outside directive
const vClickOutside = {
  mounted(el: HTMLElement, binding: { value: () => void }) {
    ;(el as any).__clickOutsideHandler__ = (event: MouseEvent) => {
      if (!el.contains(event.target as Node) && !(el.previousElementSibling?.contains(event.target as Node))) {
        binding.value()
      }
    }
    document.addEventListener('pointerdown', (el as any).__clickOutsideHandler__)
  },
  unmounted(el: HTMLElement) {
    document.removeEventListener('pointerdown', (el as any).__clickOutsideHandler__)
  }
}
</script>

<style scoped>
/* === Figma 4783:134648/651/654/657 Mobile Top Action Buttons === */
.header-pill-btn {
  width: 52px;
  height: 58px;
  padding: 0;
  border-radius: 8px;
  background: #FFFFFF;
  border: 1.5px solid #E6E5E0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  box-shadow:
    0 3px 10px rgba(20, 20, 22, 0.1),
    0 1px 1px rgba(0, 0, 0, 0.1),
    0 3px 3px rgba(0, 0, 0, 0.09),
    0 6px 4px rgba(0, 0, 0, 0.05),
    0 11px 4px rgba(0, 0, 0, 0.02);
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
  touch-action: manipulation;
  overflow: visible;
}

.header-pill-btn:active {
  transform: scale(0.96);
}

.header-pill-btn.active {
  background: #E8F1FE;
  border-color: #BCD9FB;
}

.header-pill-btn.active .btn-label {
  color: #1F6FE0;
}

.header-pill-btn:disabled {
  opacity: 0.5;
  pointer-events: none;
}

/* Icon: sized per Figma vector natural dimensions */
.icon-wrap {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-wrap img {
  display: block;
  object-fit: contain;
}

/* Back: 7x12 native — frame box 20x20 in Figma */
.icon-wrap--back {
  height: 20px;
  margin-top: 8px;
}
.icon-wrap--back img {
  width: 7px;
  height: 12px;
}

/* Grid / View-in-Room: 21x21 / 27x21 native — frame box 21x21 in Figma */
.icon-wrap--grid {
  height: 21px;
  margin-top: 7px;
}
.icon-wrap--grid img {
  width: auto;
  height: 21px;
}

/* More: 16x27 native */
.icon-wrap--more {
  height: 27px;
  margin-top: 4px;
}
.icon-wrap--more img {
  width: 16px;
  height: 27px;
}

/* Single-line label (Back, More) at y=button.y+33.5 — lower */
.header-pill-btn--single .btn-label {
  margin-top: auto;
  padding-bottom: 8px;
}

/* 2D / 3D View Mode Toggle on 3D Grid */
.header-pill-btn--view-toggle {
  padding: 4px 3px;
  justify-content: flex-start;
}

.view-toggle-track {
  position: relative;
  display: block;
  width: 40px;
  height: 20px;
  margin-top: 3px;
  border: 1.2px solid #e2e1dc;
  border-radius: 10px;
  background-color: #e2e1dc;
  transition: background-color 0.2s, border-color 0.2s;
  flex-shrink: 0;
}

.view-toggle-thumb {
  position: absolute;
  top: 1.5px;
  left: 1.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  box-sizing: border-box;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.header-pill-btn--view-toggle[aria-checked="true"] .view-toggle-track {
  background-color: #313558;
  border-color: #313558;
}

.header-pill-btn--view-toggle[aria-checked="true"] .view-toggle-thumb {
  transform: translateX(19.5px);
}

.view-toggle-thumb-label {
  font-family: 'Montserrat', sans-serif;
  font-size: 8px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.05px;
  color: #1C1C1E;
}

.btn-label--toggle {
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 10px;
  line-height: 12px;
  text-align: center;
  color: #1C1C1E;
  width: 100%;
  margin-top: 3px;
  padding-bottom: 5px;
}

/* Double-line label (Change Grid, View in Room) — slightly higher */
.btn-label--double {
  margin-top: 2px;
  padding-bottom: 6px;
}

.btn-label {
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 10px;
  line-height: 12px;
  text-align: center;
  color: #1C1C1E;
  white-space: nowrap;
  width: 100%;
}

.more-menu-dropdown {
  width: 210px;
  border: 1px solid #E7E6E1;
  box-shadow:
    0 3px 10px rgba(20, 20, 22, 0.1),
    0 1px 1px rgba(0, 0, 0, 0.1),
    0 3px 3px rgba(0, 0, 0, 0.09),
    0 6px 4px rgba(0, 0, 0, 0.05);
}

.fade-menu-enter-active,
.fade-menu-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.fade-menu-enter-from,
.fade-menu-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
